import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Music,
  Coins,
  Clock,
  Sparkles,
  X,
  Gift,
  Star,
  Menu,
} from "lucide-react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";
import PartnerGiftsModal from "../components/PartnerGiftsModal";
import PerformerReviewsModal from "../components/PerformerReviewsModal";
import Sidebar from "../components/ui/Sidebar";
import MobileNBottomNav from "../components/ui/MobileBottomNav";

const TRP_LEVELS = [
  { name: "Starter", minRating: 0 },
  { name: "Good", minRating: 1 },
  { name: "Very Good", minRating: 2 },
  { name: "Excellent", minRating: 3 },
];

export default function Rewards() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payoutData, setPayoutData] = useState(null);
  const [error, setError] = useState(null);

  // Tips carousel state
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [tipsHeading, setTipsHeading] = useState("Reach Very Good");
  const [tipsList, setTipsList] = useState([]);

  // Selected session for detail modal
  const [selectedSession, setSelectedSession] = useState(null);

  // Modals for Gifts breakdown & Reviews
  const [showGiftsModal, setShowGiftsModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarPage, setSidebarPage] = useState("rewards");

  const isBusiness = localStorage.getItem("isBusiness") === "true";

  const handleSidebarNav = (page) => {
    setSidebarPage(page);
    setIsSidebarOpen(false);
    switch (page) {
      case "home":
        navigate(isBusiness ? "/loggedinartistbussiness" : "/loggedinartist");
        break;
      case "calendar":
        navigate(isBusiness ? "/businessCalendar" : "/bookevent");
        break;
      case "qr":
        navigate(isBusiness ? "/businessQr" : "/me");
        break;
      case "rewards":
        navigate("/rewards");
        break;
      case "profile":
        navigate(isBusiness ? "/businessProfile" : "/artistprofile");
        break;
      default:
        break;
    }
  };

  const fetchRewards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_ENDPOINTS.PAYOUT_HISTORY}?limit=20&skip=0`, {
        method: "GET",
      });

      if (!res.ok) {
        throw new Error("Failed to load rewards and payout history");
      }

      const data = await res.json();
      setPayoutData(data);

      // Setup tips from response
      const suggestions = data.moreTipsSuggestions || data.improveRatingSuggestions || [];
      if (suggestions.length > 0) {
        setTipsHeading(suggestions[0]);
        setTipsList(suggestions.slice(1).length > 0 ? suggestions.slice(1) : [suggestions[0]]);
      } else {
        setTipsHeading("Boost Your Ratings");
        setTipsList([
          "Play popular songs during peak venue hours",
          "Engage interactively with your audience",
          "Take and queue requests promptly to avoid delays",
        ]);
      }
    } catch (err) {
      console.error("Error fetching rewards:", err);
      setError(err.message || "Could not load rewards data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  // Auto-swipe tips
  useEffect(() => {
    if (tipsList.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentTipIndex((prev) => (prev < tipsList.length - 1 ? prev + 1 : 0));
    }, 4000);
    return () => clearInterval(timer);
  }, [tipsList]);

  const handlePrevTip = () => {
    if (tipsList.length === 0) return;
    setCurrentTipIndex((prev) => (prev > 0 ? prev - 1 : tipsList.length - 1));
  };

  const handleNextTip = () => {
    if (tipsList.length === 0) return;
    setCurrentTipIndex((prev) => (prev < tipsList.length - 1 ? prev + 1 : 0));
  };

  // Determine user's TRP Level index
  const averageRatingName = payoutData?.averageRatingName || "Starter";
  const getLevelIndex = (name) => {
    switch ((name || "").toLowerCase()) {
      case "starter":
        return 0;
      case "good":
        return 1;
      case "very good":
        return 2;
      case "excellent":
        return 3;
      default:
        return 0;
    }
  };
  const currentLevelIndex = getLevelIndex(averageRatingName);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Recent Session";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-[#FAF8FC] via-white to-[#F6F1FA] w-full overflow-x-hidden relative lg:mt-0 mt-16">
      <MobileNBottomNav />

      {/* Mobile Header Toggle */}
      <div className="fixed top-0 left-0 z-50 w-full px-4 py-3 flex items-center justify-between bg-logo backdrop-blur-sm lg:hidden shadow-sm">
        <div className="flex items-center gap-2">
          <img alt="" src="/images/tringbox-logo.png" className="h-6" />
        </div>
        <motion.button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-md"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </motion.button>
      </div>

      {/* Fixed Sidebar */}
      <div
        className={`fixed left-0 top-0 w-80 h-full z-40 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar currentPage={sidebarPage} onPageChange={handleSidebarNav} />
      </div>

      {/* Main Content Area */}
      <main className="flex-1 min-h-screen lg:ml-80 flex flex-col w-full pb-20">
        {/* Top Header */}
        <div className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-purple-100/80 shadow-xs">
          <div className="max-w-5xl mx-auto px-4 py-3.5 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-[#72288D] transition-colors font-medium text-sm"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-[#72288D]" />
              <h1 className="text-lg font-bold text-gray-900">
                Rewards & Performance
              </h1>
            </div>
            <div className="w-16" />
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 space-y-6 w-full">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-3">
            <div className="w-10 h-10 border-4 border-[#72288D] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-medium text-gray-500">Loading rewards and performance...</p>
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-red-50 border border-red-200 p-8 text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <button
              onClick={fetchRewards}
              className="px-4 py-2 rounded-xl bg-[#72288D] text-white text-sm font-semibold hover:bg-[#72288D]/90"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Total Rewards Earned Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#2D0A4A] via-[#511370] to-[#72288D] p-6 md:p-8 text-white shadow-xl"
            >
              <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase font-bold tracking-wider text-purple-200">
                      Total Rewards Earned
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/15 text-purple-100">
                      As of Today
                    </span>
                  </div>
                  <div className="text-4xl md:text-5xl font-extrabold tracking-tight mt-1.5">
                    ₹{(payoutData?.totalPaidAmount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <p className="text-xs text-purple-200 mt-2">
                    Cumulative payouts received across {payoutData?.totalPayouts || 0} event sessions
                  </p>
                </div>

                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/15">
                  <div className="p-2.5 rounded-xl bg-amber-400 text-purple-950">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-purple-200">Current Standing</div>
                    <div className="text-base font-bold text-white capitalize">
                      ● {averageRatingName}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* TRP Rating Level Progression */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl bg-white p-6 shadow-md border border-purple-50 space-y-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    TRP Performance Level
                  </h3>
                  <p className="text-xs text-gray-500">
                    Earn higher rewards and perks as your rating advances
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#72288D]/10 text-[#72288D] border border-[#72288D]/20">
                  {averageRatingName}
                </span>
              </div>

              {/* Progress Tracker Bar */}
              <div className="pt-4 pb-2">
                <div className="relative flex items-start justify-between">
                  {/* Track Line Container spanning horizontally between center of first circle and center of last circle */}
                  <div className="absolute left-8 right-8 top-5 -translate-y-1/2 h-1 z-0">
                    {/* Background Track Line */}
                    <div className="w-full h-full bg-gray-200 rounded-full" />

                    {/* Active Track Line */}
                    <div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#72288D] to-purple-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${(currentLevelIndex / (TRP_LEVELS.length - 1)) * 100}%`,
                      }}
                    />
                  </div>

                  {/* Level Nodes */}
                  {TRP_LEVELS.map((level, idx) => {
                    const isCompleted = idx < currentLevelIndex;
                    const isCurrent = idx === currentLevelIndex;

                    return (
                      <div
                        key={level.name}
                        className="relative z-10 flex flex-col items-center w-16"
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-md ${
                            isCompleted
                              ? "bg-emerald-500 text-white shadow-emerald-200"
                              : isCurrent
                              ? "bg-[#72288D] text-white ring-4 ring-[#72288D]/20 scale-110 shadow-purple-300"
                              : "bg-gray-100 text-gray-400 border-2 border-gray-200"
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <span className="text-xs font-bold">{idx + 1}</span>
                          )}
                        </div>
                        <span
                          className={`text-xs mt-2 font-semibold text-center whitespace-nowrap ${
                            isCurrent
                              ? "text-[#72288D]"
                              : isCompleted
                              ? "text-gray-800"
                              : "text-gray-400"
                          }`}
                        >
                          {level.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Quick Access Action Cards for Gifts & Reviews */}
            <div className={`grid grid-cols-1 ${!isBusiness ? "sm:grid-cols-2" : ""} gap-4`}>
              {/* Fan Tips & Gifts Breakdown Card (Hidden for business users) */}
              {!isBusiness && (
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => setShowGiftsModal(true)}
                  className="cursor-pointer rounded-3xl p-5 bg-gradient-to-br from-purple-50 via-white to-pink-50/50 border border-purple-100 shadow-xs hover:shadow-md transition-all flex items-center justify-between"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#72288D] to-[#9C38BF] flex items-center justify-center text-white shadow-md shadow-purple-500/20">
                      <Gift className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">
                        Gifts & Tips Breakdown
                      </h4>
                      <p className="text-xs text-gray-500">
                        Itemized coin gifts, direct tips & payouts
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-[#72288D] bg-purple-100/70 hover:bg-purple-100 px-3 py-1.5 rounded-xl transition-colors">
                    View →
                  </span>
                </motion.div>
              )}
            </div>

            {/* Tips Auto-Carousel Card */}
            {tipsList.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-50 via-pink-50/40 to-purple-50 p-6 border border-purple-100 shadow-xs"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-[#72288D]">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">
                      {tipsHeading}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400 font-medium">
                      {currentTipIndex + 1}/{tipsList.length}
                    </span>
                    <button
                      type="button"
                      onClick={handlePrevTip}
                      className="p-1 rounded-full hover:bg-white text-gray-600 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleNextTip}
                      className="p-1 rounded-full hover:bg-white text-gray-600 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="min-h-[44px] flex items-center">
                  <p className="text-sm md:text-base font-medium text-gray-800 leading-snug">
                    "{tipsList[currentTipIndex]}"
                  </p>
                </div>
              </motion.div>
            )}

            {/* Sessions Payout History List */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Session History</h3>
                <span className="text-xs text-gray-500 font-medium">
                  {payoutData?.payouts?.length || 0} Sessions recorded
                </span>
              </div>

              {(!payoutData?.payouts || payoutData.payouts.length === 0) ? (
                <div className="rounded-2xl bg-white p-10 text-center border border-gray-100">
                  <Music className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <h4 className="text-base font-bold text-gray-800">No Past Sessions Yet</h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                    Complete live venue events to receive audience song requests, tips, and performance rating payouts.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payoutData.payouts.map((session, idx) => (
                    <motion.div
                      key={session._id || session.id || idx}
                      whileHover={{ scale: 1.008 }}
                      whileTap={{ scale: 0.995 }}
                      onClick={() => setSelectedSession(session)}
                      className="cursor-pointer rounded-2xl bg-white p-5 border border-purple-50 shadow-xs hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                    >
                      {/* Left side info */}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 text-base">
                            {session.djName || "Live Performance"}
                          </span>
                          <span
                            className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                              session.status === "SUCCESS"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {session.status || "COMPLETED"}
                          </span>
                          {session.ratingName && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                              ★ {session.ratingName}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            {formatDate(session.payoutDate || session.createdAt)}
                          </span>
                          <span className="flex items-center gap-1 text-emerald-600 font-medium">
                            <Music className="w-3.5 h-3.5" />
                            {session.songsPlayed || 0} Played
                          </span>
                          {session.songsRejected > 0 && (
                            <span className="text-rose-500">
                              {session.songsRejected} Rejected
                            </span>
                          )}
                          {session.djTipAmount > 0 && (
                            <span className="flex items-center gap-1 text-amber-600 font-medium">
                              <Coins className="w-3.5 h-3.5" />
                              +₹{session.djTipAmount} Tips
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right side amount */}
                      <div className="flex md:flex-col items-center md:items-end justify-between border-t md:border-t-0 pt-3 md:pt-0 border-gray-100">
                        <div className="text-lg font-extrabold text-[#72288D]">
                          ₹{(session.finalAmount || 0).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          View details <ChevronRight className="w-3 h-3" />
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Session Detail Modal */}
      <AnimatePresence>
        {selectedSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl border border-purple-100"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-white">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Session Breakdown</h3>
                  <p className="text-xs text-gray-500">
                    {formatDate(selectedSession.payoutDate || selectedSession.createdAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSession(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-5">
                {/* Net Payout Banner */}
                <div className="rounded-2xl bg-gradient-to-r from-[#2D0A4A] to-[#72288D] p-5 text-white text-center">
                  <div className="text-xs text-purple-200">Net Amount Paid</div>
                  <div className="text-3xl font-black mt-1">
                    ₹{(selectedSession.finalAmount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  {selectedSession.ratingName && (
                    <div className="inline-block mt-2 px-3 py-0.5 rounded-full bg-white/20 text-xs font-semibold">
                      Performance: {selectedSession.ratingName}
                    </div>
                  )}
                </div>

                {/* Earnings & Penalties Breakdown */}
                <div className="rounded-2xl bg-gray-50 p-4 space-y-2.5 text-sm border border-gray-100">
                  <div className="flex justify-between text-gray-600">
                    <span>Base Amount</span>
                    <span className="font-semibold text-gray-900">
                      ₹{selectedSession.baseAmount || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tips Received</span>
                    <span className="font-semibold text-emerald-600">
                      +₹{selectedSession.djTipAmount || 0}
                    </span>
                  </div>
                  {selectedSession.penaltyAmount > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Penalties Deducted</span>
                      <span className="font-semibold text-rose-500">
                        -₹{selectedSession.penaltyAmount}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
                    <span>Total Final Payout</span>
                    <span className="text-[#72288D]">₹{selectedSession.finalAmount || 0}</span>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2.5 rounded-xl bg-purple-50/60 border border-purple-100">
                    <div className="text-xs text-gray-500">Played</div>
                    <div className="text-base font-bold text-[#72288D]">
                      {selectedSession.songsPlayed || 0}
                    </div>
                  </div>
                  <div className="p-2.5 rounded-xl bg-purple-50/60 border border-purple-100">
                    <div className="text-xs text-gray-500">Rejected</div>
                    <div className="text-base font-bold text-rose-600">
                      {selectedSession.songsRejected || 0}
                    </div>
                  </div>
                  <div className="p-2.5 rounded-xl bg-purple-50/60 border border-purple-100">
                    <div className="text-xs text-gray-500">Overwaited</div>
                    <div className="text-base font-bold text-amber-600">
                      {selectedSession.songsOverWaited || 0}
                    </div>
                  </div>
                </div>

                {/* Rating Score Breakdown if present */}
                {selectedSession.ratingBreakdown && (
                  <div className="space-y-2 pt-1">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">
                      Rating Score Breakdown
                    </div>
                    <div className="space-y-1.5 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Song Rejection Score:</span>
                        <span className="font-semibold">
                          {selectedSession.ratingBreakdown.rejectedScore || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Queue Timing Score:</span>
                        <span className="font-semibold">
                          {selectedSession.ratingBreakdown.overWaitedScore || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Crowd Tipping Score:</span>
                        <span className="font-semibold">
                          {selectedSession.ratingBreakdown.tipScore || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Performance Score:</span>
                        <span className="font-bold text-[#72288D]">
                          {selectedSession.ratingBreakdown.total || 0} / 100
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedSession(null)}
                  className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-[#72288D] hover:bg-[#72288D]/90 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </main>

      {/* Partner Gifts Breakdown Modal */}
      {!isBusiness && (
        <PartnerGiftsModal
          isOpen={showGiftsModal}
          onClose={() => setShowGiftsModal(false)}
        />
      )}
    </div>
  );
}
